import { useEffect, useMemo, useState } from "react";
import { useMedia } from "react-use";
import { Link, useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";
import { capitalize, debounce } from "lodash";
import posthog from "posthog-js";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Frame, Size } from "../../types/product";
import { formattedPrice } from "../../helpers/money";
import Button from "../../components/Button";
import { User } from "../../types/user";
import SelectButton from "./SelectButton";
import { useAppState } from "../../state";
import { media } from "../../helpers/layout";
import { colors, fonts } from "../../theme";
import { getAvailableSizes, getSizeAndFrame, sizeLabelsForCountry } from "../../helpers/product";
import InfoAccordion from "./InfoAccordion";
import { CountryCode, ProductPageProductFragment } from "../../generated/storefront";
import Cards from "./Cards";
import useBasketActions from "../../hooks/useBasketActions";
import unframedSwatch from "../../assets/images/unframedSwatch.png";
import { FrameType, getFutureDiscountPct } from "../../helpers/basket";
import { getIdNumber } from "../../helpers/shopify";
import { Icon } from "../../components/Icon";
import MountDialog from "./MountDialog";
import BoxFrameDialog from "./BoxFrameDialog";
import MountSwatch from "./MountSwatch";
import NoMountSwatch from "./NoMountSwatch";
import useAttributes from "./useAttributes";

export type CheckoutState = {
  material: "print" | "canvas";
  quantity: number;
  size?: Size;
  frame?: Frame;
  boxFrame: boolean;
  mounted: boolean;
  denominations?: string;
} & { [key: string]: string | number | boolean };

type Props = {
  product: ProductPageProductFragment;
  artist?: User | null;
  state: CheckoutState;
  setState: (state: CheckoutState) => void;
  setEdited: (edited: boolean) => void;
};

const disclaimerStyles = {
  fontSize: 10,
  lineHeight: 1,
  color: "primary",
  fontFamily: fonts.body,
  paddingX: 0.5,
  paddingTop: 0.4,
  borderRadius: 1,
};

const SideBar = ({ product, artist, state, setState, setEdited }: Props) => {
  const { basket, selectedCountry, navHeight, isMobileScreen } = useAppState();
  const priceDiffFlag = posthog.getFeatureFlag("display-price-difference") === "test";
  const [itemIsOpen, setItemIsOpen] = useState(false);
  const [stickyButtonVisible, setStickyButtonVisible] = useState(false);
  const [stickyButtonBottomPadding, setStickyButtonBottomPadding] = useState(16);
  const [searchParams, setSearchParams] = useSearchParams();
  const variantId = searchParams.get("variant");
  const frame = (searchParams.get("frame") || Frame.Unframed) as Frame;
  const mount = searchParams.get("mount");
  const frameType = searchParams.get("frameType");
  const material = searchParams.get("material");
  const variants = product.variants.nodes;
  const { addOrCreateBasket, loading, error } = useBasketActions();
  const [mountDialogOpen, setMountDialogOpen] = useState(false);
  const [boxFrameDialogOpen, setBoxFrameDialogOpen] = useState(false);
  const smallDevice = useMedia(media.s);
  const mountLabel = match(selectedCountry)
    .with(CountryCode.Us, () => "Matt")
    .otherwise(() => "Mount");
  const selectedVariant = useMemo(
    () =>
      variants.find((variant) =>
        variant.selectedOptions.every((option) => {
          if (option.name === "frame") return option.value === Frame.Unframed;
          return state[option.name] === option.value;
        })
      ) || product.selectedOrFirstAvailableVariant,
    [variants, state, product.selectedOrFirstAvailableVariant]
  );
  const smallestSize = getAvailableSizes(product.variants.nodes)[0];
  const {
    framePrice,
    mountingPrice,
    boxFramePrice,
    boxFramePriceDiff,
    availabileFrames,
    standardFrameSwatch,
    boxFrameSwatch,
    mountSwatch,
    noMountSwatch,
    canvasSwatches,
    canvasPrice,
  } = useAttributes({ state, selectedCountry });

  const addToBasket = () => {
    if (!selectedVariant) return;
    return addOrCreateBasket([
      {
        type: state.material,
        merchandiseId: selectedVariant.id,
        quantity: state.quantity,
        frame: state.frame || Frame.Unframed,
        size: state.size || smallestSize,
        mounted: state.mounted,
        boxFrame: state.boxFrame,
      },
    ]);
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    if (name === "material" && value === "canvas") {
      setState({ ...state, material: value, mounted: false, boxFrame: false });
    } else if (name === "frame" && value === Frame.Unframed) {
      setState({ ...state, frame: value, mounted: false, boxFrame: false });
    } else {
      setState({ ...state, [name]: value });
    }
    setEdited(true);
  };

  const toggleMounted = () => {
    if (state.frame === Frame.Unframed && !state.mounted) return setState({ ...state, mounted: true, frame: Frame.Natural });
    setState({ ...state, mounted: !state.mounted });
  };

  const toggleBoxFrame = () => {
    setState({ ...state, boxFrame: !state.boxFrame });
  };

  useEffect(() => {
    const showCanvas = material === "canvas" && selectedCountry !== CountryCode.Au;
    if (variantId) {
      const paramVariant = variants.find((v) => getIdNumber(v.id, "ProductVariant") === variantId)?.selectedOptions;
      if (!paramVariant) return setState({ ...state, size: smallestSize, frame: Frame.Unframed });
      const { size } = getSizeAndFrame(paramVariant);
      setState({
        ...state,
        size,
        frame,
        mounted: mount === "yes",
        boxFrame: frameType === "premium",
        material: showCanvas ? "canvas" : "print",
      });
    } else {
      setState({
        ...state,
        size: smallestSize,
        frame: showCanvas ? Frame.Natural : Frame.Unframed,
        mounted: false,
        boxFrame: false,
        material: showCanvas ? "canvas" : "print",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants]);

  useEffect(() => {
    const selectedVariant = variants.find((variant) => {
      const { size } = getSizeAndFrame(variant.selectedOptions);
      return size === state.size;
    });

    if (!selectedVariant) return;

    if (
      state.size &&
      state.size === smallestSize &&
      state.frame &&
      state.frame === Frame.Unframed &&
      state.material === "print"
    ) {
      if (searchParams.size > 0) {
        searchParams.delete("variant");
        searchParams.delete("mount");
        searchParams.delete("frame");
        searchParams.delete("boxFrame");
        searchParams.delete("canvas");
        setSearchParams(searchParams, { preventScrollReset: true });
      }
    } else {
      setSearchParams(
        {
          variant: getIdNumber(selectedVariant.id, "ProductVariant"),
          frame: state.frame || Frame.Unframed,
          ...(state.mounted ? { mount: "yes" } : {}),
          ...(state.boxFrame ? { frameType: "premium" } : {}),
          ...(state.material === "canvas" ? { material: "canvas" } : {}),
        },
        { preventScrollReset: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, state.size, state.frame, state.mounted, state.boxFrame, state.material]);

  const setStickyButtonVisibility = debounce(() => {
    const scrollThreshold = 600;
    if (isMobileScreen) {
      setStickyButtonVisible(window.scrollY > navHeight + scrollThreshold);
    } else {
      setStickyButtonVisible(false);
    }
  }, 100);

  useEffect(() => {
    if (selectedCountry === CountryCode.Au) {
      setState({ ...state, material: "print", boxFrame: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => {
    if (!isMobileScreen) return;
    window.addEventListener("scroll", setStickyButtonVisibility);

    return () => {
      window.removeEventListener("scroll", setStickyButtonVisibility);
    };
  }, [isMobileScreen, setStickyButtonVisibility]);

  let lastHeight = window.innerHeight;

  const adjustStickyButtonPadding = debounce(() => {
    const newHeight = window.innerHeight;
    if (newHeight < lastHeight) {
      setStickyButtonBottomPadding(16);
    } else if (newHeight > lastHeight) {
      setStickyButtonBottomPadding(32);
    }
    lastHeight = newHeight;
  }, 100);

  useEffect(() => {
    window.addEventListener("resize", adjustStickyButtonPadding);

    return () => {
      window.removeEventListener("resize", adjustStickyButtonPadding);
    };
  });

  const discountPct = getFutureDiscountPct(basket);
  const showDiscountedPrices = discountPct > 0;
  const currencyCode = useMemo(() => variants[0].price.currencyCode, [variants]);
  const selectedVariantPrice = useMemo(() => {
    const mounting = state.mounted ? mountingPrice : 0;
    const boxFrame = state.boxFrame ? boxFramePrice : 0;
    const frame = state.boxFrame ? boxFrame : framePrice;
    const canvas = state.material === "canvas";
    return Number(selectedVariant?.price.amount) + (canvas ? canvasPrice : mounting + frame);
  }, [selectedVariant, state, mountingPrice, boxFramePrice, framePrice, canvasPrice]);

  const sizeLabels = sizeLabelsForCountry(selectedCountry);
  const availabileSizes = getAvailableSizes(product.variants.nodes);

  if (!selectedVariant) return null;

  const renderAddToCartButton = () => (
    <Button
      fullWidth
      onClick={addToBasket}
      disabled={!(state.size && state.frame && state.quantity)}
      loading={loading}
      size="large"
    >
      <Stack gap={0.75} direction="row" justifyContent="center" alignItems="center">
        <Typography color="inherit">Add to cart ·</Typography>
        <Typography
          style={{ whiteSpace: "nowrap", textDecoration: showDiscountedPrices ? "line-through" : "none" }}
          color={showDiscountedPrices ? colors.grey60 : "inherit"}
          align="right"
        >
          {formattedPrice(selectedVariantPrice, currencyCode)}
        </Typography>
        {showDiscountedPrices && (
          <Typography color="inherit" style={{ whiteSpace: "nowrap" }}>
            {formattedPrice(Number(selectedVariantPrice) * (1 - discountPct), currencyCode)}
          </Typography>
        )}
      </Stack>
    </Button>
  );

  return (
    <Stack width={{ xs: "100%", md: "33%" }}>
      <Stack gap={{ xs: 1.5, md: 2 }} height="100%">
        <Box paddingX={{ xs: 2, md: 3 }} paddingTop={{ xs: 1.5, md: 0 }}>
          <Stack justifyContent="space-between" direction="row" gap={3}>
            <Stack gap={0.5}>
              {isMobileScreen ? (
                <Typography component="h1">{product.title}</Typography>
              ) : (
                <Stack gap={1} direction="row" alignItems="center">
                  <Typography variant="h5" component="h1">
                    {product.title}
                  </Typography>
                </Stack>
              )}

              {artist ? (
                <Link to={`/artists/${artist.permalink || artist.id}`}>
                  <Typography fontSize={14} style={{ textDecoration: "underline" }}>
                    {artist.firstName} {artist.lastName}
                  </Typography>
                </Link>
              ) : (
                <CircularProgress size={14} />
              )}
            </Stack>

            <Stack gap={0.5} alignItems="end">
              <Stack gap={1} direction="row">
                <Typography
                  fontSize={{ xs: 14, md: 16 }}
                  style={{ whiteSpace: "nowrap", textDecoration: showDiscountedPrices ? "line-through" : "none" }}
                  color={showDiscountedPrices ? colors.grey60 : "primary"}
                  align="right"
                >
                  {formattedPrice(selectedVariantPrice, currencyCode)}
                </Typography>
                {showDiscountedPrices && (
                  <Typography fontSize={{ xs: 14, md: 16 }} align="right" style={{ whiteSpace: "nowrap" }}>
                    {formattedPrice(Number(selectedVariantPrice) * (1 - discountPct), currencyCode)}
                  </Typography>
                )}
              </Stack>
              <a href="https://www.reviews.co.uk/company-reviews/store/www.goodmoodprints.io" target="_blank" rel="noreferrer">
                <Stack gap={1} direction="row" alignItems="center" paddingTop={0.25}>
                  <Typography whiteSpace="nowrap" fontSize={{ xs: 12, md: 14 }}>
                    4.9 / 5
                  </Typography>
                  <Stack direction="row" gap={0.5} justifyContent={{ xs: "center", md: "start" }}>
                    {[
                      ...Array(5)
                        .fill("")
                        .map((r, i) => <Icon key={i} icon="star" size={12} fill={colors.gold} />),
                    ]}
                  </Stack>
                </Stack>
              </a>
            </Stack>
          </Stack>
        </Box>

        <Box
          position={{ xs: "relative", md: itemIsOpen ? "relative" : "sticky" }}
          top={{ xs: 0, md: itemIsOpen ? 0 : navHeight }}
          zIndex={1}
          bgcolor={colors.white}
        >
          <Box paddingX={{ xs: 2, md: 3 }}>
            <Stack gap={1.5} height="100%">
              {product.availableForCanvas?.value === "true" && selectedCountry !== CountryCode.Au && (
                <Stack gap={1}>
                  <Typography fontSize={12}>Material</Typography>
                  <Stack direction="row" gap={1} onChange={handleChange}>
                    <SelectButton
                      type="radio"
                      name="material"
                      key="print"
                      label="Art print"
                      id="print"
                      checked={state.material === "print"}
                      value="print"
                    />
                    <SelectButton
                      type="radio"
                      name="material"
                      key="canvas"
                      label="Canvas"
                      id="canvas"
                      checked={state.material === "canvas"}
                      value="canvas"
                    />
                  </Stack>
                </Stack>
              )}
              {!isMobileScreen && (
                <Stack gap={1} paddingTop={0.75}>
                  <Typography fontSize={12}>
                    Size:{" "}
                    {state.size
                      ? sizeLabels[state.size][
                          state.frame === Frame.Unframed ? ("printMeasurements" as const) : ("framedMeasurements" as const)
                        ]
                      : ""}
                  </Typography>
                  <Stack onChange={handleChange} direction="row" gap={1}>
                    {availabileSizes.map((size) => (
                      <SelectButton
                        type="radio"
                        name="size"
                        key={size}
                        label={sizeLabels[size].shortHand}
                        disclaimer={
                          priceDiffFlag && (
                            <Typography bgcolor={colors.white} {...disclaimerStyles}>
                              {formattedPrice(
                                variants.find((v) => v.selectedOptions[0].value === size)?.price.amount,
                                currencyCode,
                                false
                              )}
                            </Typography>
                          )
                        }
                        id={size}
                        checked={state.size === size}
                        value={size}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}

              <Stack gap={1}>
                <Typography fontSize={12}>Frame: {capitalize(state.frame)}</Typography>
                <Stack onChange={handleChange} direction="row" gap={1}>
                  <SelectButton
                    type="radio"
                    name="frame"
                    key="unframed"
                    disclaimer={
                      <Typography bgcolor={colors.white} {...disclaimerStyles}>
                        Unframed
                      </Typography>
                    }
                    image={state.material === "canvas" ? canvasSwatches.unframed?.src : unframedSwatch}
                    id="unframed"
                    checked={state.frame === Frame.Unframed}
                    value={Frame.Unframed}
                    fullWidth={smallDevice}
                  />
                  {availabileFrames.map((frame) => (
                    <SelectButton
                      type="radio"
                      name="frame"
                      key={frame.id}
                      disclaimer={
                        <Stack alignItems="center">
                          <Typography bgcolor={colors.white} {...disclaimerStyles}>
                            {capitalize(frame.name)}
                          </Typography>
                          {priceDiffFlag && <Typography {...disclaimerStyles}>+{framePrice}</Typography>}
                        </Stack>
                      }
                      image={
                        state.material === "canvas"
                          ? canvasSwatches[frame.name as Frame]?.src
                          : frame.swatch?.image?.previewImage?.url
                      }
                      id={frame.id}
                      checked={state.frame === frame.name}
                      value={frame.name}
                      fullWidth={smallDevice}
                      imagePosition="70%"
                    />
                  ))}
                </Stack>
              </Stack>

              {state.frame !== Frame.Unframed && state.material === "print" && (
                <Stack direction="row" gap={1.25} paddingTop={0.75}>
                  {selectedCountry !== CountryCode.Au && (
                    <Stack gap={1} width={smallDevice ? "100%" : "auto"}>
                      <Typography fontSize={12} display="flex" alignItems="center">
                        Frame type: {state.boxFrame ? capitalize(FrameType.Premium) : capitalize(FrameType.Standard)}
                        <InfoOutlined
                          onClick={() => setBoxFrameDialogOpen(true)}
                          fontSize="inherit"
                          style={{ cursor: "pointer", marginLeft: 4, color: colors.grey40 }}
                        />
                      </Typography>
                      <Stack onChange={toggleBoxFrame} direction="row" gap={1}>
                        <SelectButton
                          type="radio"
                          id="classic"
                          key="classic"
                          name="frameType"
                          image={standardFrameSwatch?.src}
                          disclaimer={
                            <Typography bgcolor={colors.white} {...disclaimerStyles}>
                              {capitalize(FrameType.Standard)}
                            </Typography>
                          }
                          checked={!state.boxFrame}
                          fullWidth={smallDevice}
                        />
                        <SelectButton
                          type="radio"
                          id="box"
                          key="box"
                          name="frameType"
                          image={boxFrameSwatch?.src}
                          disclaimer={
                            <Stack alignItems="center">
                              <Typography bgcolor={colors.white} {...disclaimerStyles}>
                                {capitalize(FrameType.Premium)}
                              </Typography>
                              {priceDiffFlag && boxFramePriceDiff && (
                                <Typography {...disclaimerStyles}>+{formattedPrice(boxFramePriceDiff, currencyCode)}</Typography>
                              )}
                            </Stack>
                          }
                          checked={state.boxFrame}
                          fullWidth={smallDevice}
                        />
                      </Stack>
                    </Stack>
                  )}
                  <Stack gap={1} width={smallDevice ? "100%" : "auto"}>
                    <Typography fontSize={12} display="flex" alignItems="center">
                      {mountLabel}: {state.mounted ? "Yes" : "No"}
                      <InfoOutlined
                        onClick={() => setMountDialogOpen(true)}
                        fontSize="inherit"
                        style={{ cursor: "pointer", marginLeft: 4, color: colors.grey40 }}
                      />
                    </Typography>

                    <Stack onChange={toggleMounted} direction="row" gap={1}>
                      <SelectButton
                        type="radio"
                        id="not mounted"
                        key="not mounted"
                        name="mounted"
                        image={<NoMountSwatch productImage={product.images.nodes[0]} noMountSwatchImage={noMountSwatch?.src} />}
                        disclaimer={
                          <Typography bgcolor={colors.white} {...disclaimerStyles}>
                            No {mountLabel}
                          </Typography>
                        }
                        checked={!state.mounted}
                        fullWidth={smallDevice}
                      />
                      <SelectButton
                        type="radio"
                        id="mounted"
                        key="mounted"
                        name="mounted"
                        image={<MountSwatch productImage={product.images.nodes[0]} mountSwatchImage={mountSwatch?.src} />}
                        disclaimer={
                          <Stack alignItems="center">
                            <Typography bgcolor={colors.white} {...disclaimerStyles}>
                              {mountLabel}
                            </Typography>
                            {priceDiffFlag && mountingPrice && (
                              <Typography {...disclaimerStyles}>+{formattedPrice(mountingPrice, currencyCode)}</Typography>
                            )}
                          </Stack>
                        }
                        checked={state.mounted}
                        fullWidth={smallDevice}
                      />
                    </Stack>
                  </Stack>

                  {selectedCountry === CountryCode.Au && (
                    <Stack direction="row" gap={1} width={smallDevice ? "100%" : "auto"}>
                      <Box width={smallDevice ? "100%" : "auto"} height="100%" />
                      <Box width={smallDevice ? "100%" : "auto"} height="100%" />
                    </Stack>
                  )}
                </Stack>
              )}

              {isMobileScreen && (
                <Stack gap={1} display={{ xs: "flex", md: "none" }} paddingTop={0.75}>
                  <Typography fontSize={12}>
                    Size:{" "}
                    {state.size
                      ? sizeLabels[state.size][
                          state.frame === Frame.Unframed ? ("printMeasurements" as const) : ("framedMeasurements" as const)
                        ]
                      : ""}
                  </Typography>
                  <Stack onChange={handleChange} direction="row" gap={1}>
                    {availabileSizes.map((size) => (
                      <SelectButton
                        type="radio"
                        name="size"
                        key={size}
                        label={sizeLabels[size].shortHand}
                        disclaimer={
                          priceDiffFlag && (
                            <Typography bgcolor={colors.white} {...disclaimerStyles}>
                              {formattedPrice(
                                variants.find((v) => v.selectedOptions[0].value === size)?.price.amount,
                                currencyCode,
                                false
                              )}
                            </Typography>
                          )
                        }
                        id={size}
                        checked={state.size === size}
                        value={size}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}

              <Stack paddingY={2} gap={1.5}>
                {renderAddToCartButton()}
                <Typography variant="caption" align="center" color="text.secondary" fontSize={{ xs: 12, md: 14 }}>
                  No import fees for UK, EU, AUS & USA
                </Typography>
                {error && <Typography color={colors.error}>{error}</Typography>}
              </Stack>
            </Stack>
          </Box>
        </Box>

        <div>
          <Cards />
          <InfoAccordion product={product} artist={artist} setItemIsOpen={setItemIsOpen} material={state.material} />
        </div>

        {isMobileScreen && (
          <Box
            position="fixed"
            bottom={0}
            bgcolor={colors.white}
            padding={2}
            paddingBottom={`${stickyButtonBottomPadding}px`}
            width="100%"
            zIndex={10}
            sx={{ transform: stickyButtonVisible ? "translateY(0)" : "translateY(100%)", transition: "all 0.3s" }}
          >
            {renderAddToCartButton()}
          </Box>
        )}

        <MountDialog dialogOpen={mountDialogOpen} setDialogOpen={setMountDialogOpen} selectedCountry={selectedCountry} />
        <BoxFrameDialog dialogOpen={boxFrameDialogOpen} setDialogOpen={setBoxFrameDialogOpen} selectedCountry={selectedCountry} />
      </Stack>
    </Stack>
  );
};

export default SideBar;
